import React, { useState } from 'react';
import apiClient from '../../services/apiClient';
import StakeTable from './StakeTable';
import { Button, Modal, FormInput, Toggle, Badge } from '../../components/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

const fetchProvidersAPI = async () => {
  const token = localStorage.getItem('token');
  const res = await apiClient.get('/api/game-engine/admin/providers', { headers: { Authorization: `Bearer ${token}` } });
  return res.data.success ? res.data.data : [];
};

interface Category {
  id: number;
  name: string;
  sort_order: number;
  api_is_enabled: boolean;
  admin_is_enabled: boolean;
}

const fetchCategoriesAPI = async (): Promise<Category[]> => {
  const token = localStorage.getItem("token");
  const res = await apiClient.get(`/api/game-engine/admin/categories`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.data : [];
};

const Categories: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(50);

  const { data: categories = [], isLoading } = useQuery({ queryKey: ['categories'], queryFn: fetchCategoriesAPI });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Category>>({});
  const [isEdit, setIsEdit] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [manageGamesCategoryId, setManageGamesCategoryId] = useState<number | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<number | ''>('');
  
  const { data: providers = [] } = useQuery({ queryKey: ['providers'], queryFn: fetchProvidersAPI });
  
  const assignMutation = useMutation({
    mutationFn: async (provider_id: number) => {
      const token = localStorage.getItem('token');
      return apiClient.post(`/api/game-engine/admin/categories/${manageGamesCategoryId}/assign-games`, { provider_id }, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: (res) => {
      toast.success(res.data.message || 'Games assigned to category!');
      setManageGamesCategoryId(null);
      setSelectedProviderId('');
    },
    onError: () => toast.error('Failed to assign games')
  });

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Category>) => {
      const token = localStorage.getItem("token");
      if (data.id) return apiClient.put(`/api/game-engine/admin/categories/${data.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      return apiClient.post(`/api/game-engine/admin/categories`, data, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success(variables.id ? "Category updated!" : "Category created!");
      closeModal();
    },
    onError: () => toast.error("Failed to save category")
  });

  const openModal = (cat?: Category) => {
    if (cat) { setFormData(cat); setIsEdit(true); }
    else { setFormData({ name: '', sort_order: 0, admin_is_enabled: true, api_is_enabled: true }); setIsEdit(false); }
    setIsModalOpen(true);
  };

  const closeModal = () => { setIsModalOpen(false); setFormData({}); };
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); saveMutation.mutate(formData); };

  const filtered = categories.filter((a: any) => !searchTerm || a.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filtered.length / limit));
  const paginated = filtered.slice((page - 1) * limit, page * limit);

  const columns = [
    { key: 'id', label: 'ID', width: 'w-16' },
    { key: 'name', label: 'Name' },
    { key: 'game_count', label: 'Games Count', render: (val: number) => <span className="font-bold text-slate-300">{val || 0}</span> },
    { key: 'sort_order', label: 'Sort Order' },
    {
      key: 'admin_is_enabled', label: 'Admin Status',
      render: (val: boolean, item: Category) => <Toggle checked={val} onChange={(c) => saveMutation.mutate({ ...item, admin_is_enabled: c })} />
    },
    {
      key: 'api_is_enabled', label: 'API Status',
      render: (val: boolean) => <Badge variant={val ? "success" : "danger"}>{val ? "Active" : "Down"}</Badge>
    },
    {
      key: 'actions', label: 'Actions', textAlign: 'right' as const,
      render: (_: any, item: Category) => (
        <div className="flex items-center justify-end gap-2">
                    <Button variant="primary" size="xs" onClick={() => setManageGamesCategoryId(item.id)}>Manage Games</Button>
          <Button variant="ghost" size="xs" onClick={() => openModal(item)}>Edit</Button>
        </div>
      )
    }
  ];

  return (
    <>
      <StakeTable
        title="Game Categories"
        columns={columns}
        data={paginated}
        isLoading={isLoading}
        searchPlaceholder="Search categories..."
        onSearch={(q) => { setSearchTerm(q); setPage(1); }}
        page={page}
        totalPages={totalPages}
        total={filtered.length}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        emptyMessage="No categories found."
        actions={<Button variant="success" onClick={() => openModal()}>+ Add Category</Button>}
      />

            <Modal open={isModalOpen} onClose={closeModal} title={isEdit ? 'Edit Category' : 'Add Category'}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closeModal}>Cancel</Button>
            <Button variant="primary" size="sm" loading={saveMutation.isPending} onClick={handleSubmit}>{isEdit ? 'Save Changes' : 'Create'}</Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput label="Name" required value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} />
          <FormInput type="number" label="Sort Order" required value={formData.sort_order || 0} onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) })} />
        </form>
      </Modal>

      <Modal open={manageGamesCategoryId !== null} onClose={() => setManageGamesCategoryId(null)} title="Assign Games to Category"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setManageGamesCategoryId(null)}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={!selectedProviderId} loading={assignMutation.isPending} onClick={() => assignMutation.mutate(Number(selectedProviderId))}>Assign Provider Games</Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-300">Select a Provider to bulk-assign all its active games into this category. You can also assign games manually via the Games Management page.</p>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Provider</label>
            <select required value={selectedProviderId} onChange={e => setSelectedProviderId(e.target.value ? Number(e.target.value) : '')} className="w-full p-2.5 bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 border rounded-lg text-white outline-none">
              <option value="" disabled className="bg-slate-900 text-slate-400">Select Provider</option>
              {providers.filter((p: any) => p.developer_is_enabled !== false).map((prov: any) => (
                <option key={prov.id} value={prov.id} className="bg-slate-900 text-slate-200">{prov.name}</option>
              ))}
            </select>
          </div>
        </div>
      </Modal>
    </>
  );
};
export default Categories;
